import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { AuthService } from '@meddata/core/services';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { Tokens } from '@meddata/interfaces';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';

const refreshTokenSubject = new BehaviorSubject<Tokens | null>(null);

/**
 * An HTTP interceptor function that handles authorization and token management for outgoing HTTP requests.
 *
 * This interceptor checks if the requested URL requires authentication. If the endpoint is related to login or token refresh,
 * it bypasses the authorization process and forwards the request as is.
 *
 * For protected endpoints:
 * - The interceptor verifies the validity of the current access token.
 * - If the access token has expired but the refresh token is still valid, it attempts to refresh the tokens.
 * - If token refreshing is successful, the updated token is appended, and the request is forwarded.
 * - If both tokens are expired, the user is logged out, and an error is thrown.
 *
 * Token refresh operations are synchronized to ensure only one update is performed at a time. Pending requests are queued and resumed
 * once the tokens are successfully renewed.
 *
 * @param req The outgoing HTTP request to be intercepted.
 * @param next The function to forward the HTTP request to the next handler in the chain.
 * @returns An Observable representing the HTTP response, either modified with authorization headers or the original response.
 * @throws An error if both the access and refresh tokens are expired.
 */
export const authInterceptor: HttpInterceptorFn = (req, next: HttpHandlerFn) => {
	const auth: AuthService = inject(AuthService);
	const router = inject(Router);
	const isChangePasswordRequest = req.url.includes('/change-password');

	if (
		req.url.includes('/login') ||
		req.url.includes('/refresh') ||
		req.url.includes('/forgot-password') ||
		req.url.includes('/reset-password')
	) {
		return next(req);
	}

	if (isTokenExpired(auth.token())) {
		if (isTokenExpired(auth.refreshToken())) {
			return expireSession(auth, router);
		} else {
			refreshTokenSubject.next(null);
			if (!auth.isTokenUpdating()) {
				return auth.updateToken().pipe(
					switchMap((newTokens: Tokens) => {
						if (newTokens) {
							refreshTokenSubject.next(newTokens);
							return next(addToken(req, auth.token()));
						}
						return expireSession(auth, router);
					}),
					catchError((err) => {
						return expireSession(auth, router, err);
					})
				);
			} else {
				return refreshTokenSubject.pipe(
					filter((token) => token !== null),
					take(1),
					switchMap(() => next(addToken(req, auth.token()))),
					catchError((err) =>
						isChangePasswordRequest ? throwError(() => err) : handleRuntimeAuthError(auth, router, err)
					)
				);
			}
		}
	}
	return next(addToken(req, auth.token())).pipe(
		catchError((err) =>
			isChangePasswordRequest ? throwError(() => err) : handleRuntimeAuthError(auth, router, err)
		)
	);
};

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
	return request.clone({
		setHeaders: { Authorization: `Bearer ${token}` }
	});
}

function isTokenExpired(token: string): boolean {
	if (!token) return true;
	const { exp }: { exp: number } = jwtDecode(token);
	return new Date().getTime() - new Date(exp * 1000).getTime() > 0;
}

function expireSession(auth: AuthService, router: Router, err?: unknown) {
	auth.logout();
	void router.navigate(['/login'], {
		queryParams: { reason: 'session_expired' },
		replaceUrl: true
	});
	return throwError(() => (err instanceof Error ? err : new Error('Session expired')));
}

function handleRuntimeAuthError(auth: AuthService, router: Router, err: any) {
	const status = err?.status ?? 0;
	const message = `${err?.error?.message ?? ''} ${err?.message ?? ''}`.toLowerCase();
	// Con la profilazione disattivata questo caso non esiste piu': l'eventuale 401
	// viene trattato come sessione scaduta dal ramo sotto.
	const isProfilingRequired =
		environment.profiling &&
		status === 401 &&
		(message.includes('profilazione non completata') || message.includes('accesso negato'));
	const isAuthLike403 =
		status === 403 &&
		(message.includes('token') ||
			message.includes('session') ||
			message.includes('expired') ||
			message.includes('jwt') ||
			message.includes('unauthoriz'));

	if (isProfilingRequired) {
		if (!router.url.startsWith('/profilazione-medico')) {
			void router.navigate(['/profilazione-medico'], { replaceUrl: true });
		}
		return throwError(() => err);
	}

	if (status === 401 || isAuthLike403) {
		return expireSession(auth, router, err);
	}
	return throwError(() => err);
}
